import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Bot, Plus, Trash2, Edit, FileText, Database, MessagesSquare, KeyRound,
  Sparkles, Upload, Loader2, CheckCircle2, XCircle, Search, Wand2, Brain,
  Globe, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const TONES = [
  { value: 'professional_warm', label: 'Professional & warm' },
  { value: 'concise_direct', label: 'Concise & direct' },
  { value: 'friendly_casual', label: 'Friendly & casual' },
  { value: 'formal_corporate', label: 'Formal & corporate' },
];

const MODELS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o mini (fast, cheap)' },
  { value: 'gpt-4o', label: 'GPT-4o (best quality)' },
];

export default function AgentsManagerPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('agents');

  // Agents
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [counsellors, setCounsellors] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // KB Manager
  const [kbAgent, setKbAgent] = useState(null);
  const [kbItems, setKbItems] = useState([]);
  const [loadingKb, setLoadingKb] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [qaQ, setQaQ] = useState('');
  const [qaA, setQaA] = useState('');
  const [savingQa, setSavingQa] = useState(false);
  const [ingestingCrm, setIngestingCrm] = useState(false);
  const [tester, setTester] = useState({ open: false, message: '', result: null, loading: false });
  const [crawlUrls, setCrawlUrls] = useState('');
  const [crawlMaxPages, setCrawlMaxPages] = useState(30);
  const [crawling, setCrawling] = useState(false);
  const [distilling, setDistilling] = useState(false);

  // AI Settings
  const [settings, setSettings] = useState(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  // ----- Agents -----
  const loadAgents = useCallback(async () => {
    try {
      const res = await api().get('/agent-platform/agents');
      setAgents(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { setAgents([]); }
    finally { setLoadingAgents(false); }
  }, [api]);

  const loadCounsellors = useCallback(async () => {
    try {
      const res = await api().get('/users');
      const list = res.data?.users || res.data || [];
      setCounsellors(Array.isArray(list) ? list : []);
    } catch (_e) { setCounsellors([]); }
  }, [api]);

  const loadUniversities = useCallback(async () => {
    try {
      const res = await api().get('/agent-platform/universities');
      setUniversities(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { setUniversities([]); }
  }, [api]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api().get('/agent-platform/settings');
      setSettings(res.data);
    } catch (_e) { /* ignore */ }
  }, [api]);

  useEffect(() => { loadAgents(); loadCounsellors(); loadUniversities(); loadSettings(); }, [loadAgents, loadCounsellors, loadUniversities, loadSettings]);

  const openEditor = (agent) => {
    setEditing(agent ? { ...agent } : {
      name: '',
      description: '',
      status: 'active',
      tone: 'professional_warm',
      model: 'gpt-4o-mini',
      counsellor_ids: [],
      university_ids: [],
      system_prompt: '',
      guardrails: 'Never quote final tuition amounts; never confirm acceptance.',
      confidence_threshold: 0.35,
      auto_ingest_crm_data: true,
    });
    setEditorOpen(true);
  };

  const saveAgent = async () => {
    if (!editing.name?.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing.id) {
        const { id: _x, ...patch } = editing;
        await api().patch(`/agent-platform/agents/${editing.id}`, patch);
        toast.success('Agent updated');
      } else {
        await api().post('/agent-platform/agents', editing);
        toast.success('Agent created');
      }
      setEditorOpen(false);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save agent');
    }
  };

  const deleteAgent = async (a) => {
    if (!confirm(`Delete agent "${a.name}" and its entire knowledge base?`)) return;
    try {
      await api().delete(`/agent-platform/agents/${a.id}`);
      toast.success('Agent deleted');
      loadAgents();
      if (kbAgent?.id === a.id) { setKbAgent(null); setKbItems([]); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleAgent = async (a) => {
    try {
      const newStatus = a.status === 'active' ? 'inactive' : 'active';
      await api().patch(`/agent-platform/agents/${a.id}`, { status: newStatus });
      toast.success(`Agent ${newStatus}`);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  // ----- KB -----
  const openKb = async (a) => {
    setKbAgent(a);
    setTab('kb');
    setCrawlUrls((a.fallback_domains || []).join('\n'));
    await loadKb(a.id);
  };

  const loadKb = async (agentId) => {
    setLoadingKb(true);
    try {
      const res = await api().get(`/agent-platform/agents/${agentId}/knowledge`);
      setKbItems(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { setKbItems([]); }
    finally { setLoadingKb(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !kbAgent) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api().post(`/agent-platform/agents/${kbAgent.id}/knowledge/upload-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      toast.success(`Ingested ${res.data.chunks_inserted} chunks from ${res.data.filename}`);
      await loadKb(kbAgent.id);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const addQa = async () => {
    if (!qaQ.trim() || !qaA.trim()) { toast.error('Both question and answer are required'); return; }
    setSavingQa(true);
    try {
      await api().post(`/agent-platform/agents/${kbAgent.id}/knowledge/qa`, { question: qaQ, answer: qaA });
      toast.success('Q&A added');
      setQaQ(''); setQaA('');
      await loadKb(kbAgent.id);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add Q&A');
    } finally { setSavingQa(false); }
  };

  const ingestCrm = async () => {
    setIngestingCrm(true);
    try {
      const res = await api().post(`/agent-platform/agents/${kbAgent.id}/knowledge/ingest-crm`);
      toast.success(`Ingested ${res.data.ingested} programs from CRM`);
      await loadKb(kbAgent.id);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ingest failed');
    } finally { setIngestingCrm(false); }
  };

  const crawlWebsite = async () => {
    const urls = crawlUrls.split(/\n|,/).map(s => s.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error('Add at least one URL or domain'); return; }
    setCrawling(true);
    try {
      // Persist domains on the agent so they're remembered + used as fallback
      await api().patch(`/agent-platform/agents/${kbAgent.id}`, {
        fallback_domains: urls,
        web_indexing_enabled: true,
      });
      await api().post(`/agent-platform/agents/${kbAgent.id}/knowledge/crawl-website`, {
        urls, max_pages: parseInt(crawlMaxPages, 10) || 30,
      });
      toast.success('Crawl started — refresh in a minute to see indexed pages');
      // Re-fetch agent metadata for status pill
      setTimeout(async () => {
        try {
          const a = await api().get(`/agent-platform/agents/${kbAgent.id}`);
          setKbAgent(a.data);
          await loadKb(kbAgent.id);
          loadAgents();
        } catch (_e) { /* ignore */ }
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Crawl failed to start');
    } finally { setCrawling(false); }
  };

  const distillNow = async () => {
    setDistilling(true);
    try {
      const res = await api().post(`/agent-platform/agents/${kbAgent.id}/knowledge/distill`, { days: 7 });
      const added = res.data?.added ?? 0;
      toast.success(added > 0 ? `Learned ${added} new Q&A from past conversations` : 'No new Q&A pairs found in the last 7 days');
      // Refresh agent metadata + KB
      try {
        const a = await api().get(`/agent-platform/agents/${kbAgent.id}`);
        setKbAgent(a.data);
        await loadKb(kbAgent.id);
        loadAgents();
      } catch (_e) { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Distillation failed');
    } finally { setDistilling(false); }
  };

  const deleteKb = async (item) => {
    if (!confirm(`Remove "${item.source_name}" from the knowledge base?`)) return;
    try {
      const params = new URLSearchParams({ source_type: item.source_type });
      if (item.source_id) params.set('source_id', item.source_id);
      else params.set('source_name', item.source_name);
      await api().delete(`/agent-platform/agents/${kbAgent.id}/knowledge?${params.toString()}`);
      toast.success('Removed');
      await loadKb(kbAgent.id);
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // ----- Tester -----
  const runTester = async () => {
    if (!tester.message.trim() || !kbAgent) return;
    setTester(t => ({ ...t, loading: true, result: null }));
    try {
      const res = await api().post(`/agent-platform/agents/${kbAgent.id}/draft-reply`, {
        phone: '+test', message: tester.message,
      });
      setTester(t => ({ ...t, loading: false, result: res.data }));
    } catch (err) {
      setTester(t => ({ ...t, loading: false, result: { error: err.response?.data?.detail || err.message } }));
    }
  };

  // ----- Settings -----
  const saveKey = async () => {
    if (!keyDraft.trim()) return;
    setSavingKey(true);
    try {
      await api().put('/agent-platform/settings', { openai_api_key: keyDraft.trim(), verify: true });
      toast.success('OpenAI key saved & verified');
      setKeyDraft('');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save key');
    } finally { setSavingKey(false); }
  };

  const clearKey = async () => {
    if (!confirm('Remove the saved OpenAI key? Your agents will fall back to the platform key.')) return;
    try {
      await api().put('/agent-platform/settings', { clear_key: true });
      toast.success('Key cleared');
      loadSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <Bot className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold">RAG Agents</h1>
            <p className="text-sm text-muted-foreground">Build, train and assign WhatsApp AI assistants powered by your knowledge base.</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="agents" data-testid="agents-tab">
            <Bot className="w-4 h-4 mr-1.5" /> Agents <Badge variant="secondary" className="ml-2">{agents.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="kb" disabled={!kbAgent} data-testid="kb-tab">
            <Brain className="w-4 h-4 mr-1.5" /> Knowledge Base {kbAgent && `· ${kbAgent.name}`}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="settings-tab">
            <KeyRound className="w-4 h-4 mr-1.5" /> AI Settings
          </TabsTrigger>
        </TabsList>

        {/* Agents tab */}
        <TabsContent value="agents" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Your Agents</h2>
            <Button onClick={() => openEditor(null)} data-testid="new-agent-btn">
              <Plus className="w-4 h-4 mr-2" /> New Agent
            </Button>
          </div>
          {loadingAgents ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : agents.length === 0 ? (
            <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
              <Bot className="w-12 h-12 mx-auto opacity-30 mb-3" />
              <p className="font-medium">No agents yet</p>
              <p className="text-sm text-muted-foreground mb-4">Create your first WhatsApp AI assistant — e.g., a "Sunderland Uni Agent".</p>
              <Button onClick={() => openEditor(null)}><Plus className="w-4 h-4 mr-2" /> Create Agent</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {agents.map((a) => (
                <div key={a.id} className="border rounded-lg p-4 hover:border-amber-300 transition" data-testid={`agent-card-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base truncate">{a.name}</h3>
                        <Badge className={a.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}>
                          {a.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description || 'No description'}</p>
                    </div>
                    <Switch checked={a.status === 'active'} onCheckedChange={() => toggleAgent(a)} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                    <div className="bg-slate-50 dark:bg-gray-800 rounded p-2">
                      <div className="text-muted-foreground">Model</div>
                      <div className="font-medium truncate">{a.model || 'gpt-4o-mini'}</div>
                    </div>
                    <div className="bg-slate-50 dark:bg-gray-800 rounded p-2">
                      <div className="text-muted-foreground">Counsellors</div>
                      <div className="font-medium">{(a.counsellor_ids || []).length}</div>
                    </div>
                    <div className="bg-slate-50 dark:bg-gray-800 rounded p-2">
                      <div className="text-muted-foreground">KB chunks</div>
                      <div className="font-medium">{a.kb_chunk_count ?? 0}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openKb(a)} data-testid={`kb-btn-${a.id}`}>
                      <Database className="w-3.5 h-3.5 mr-1.5" /> Knowledge
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditor(a)}>
                      <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => deleteAgent(a)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* KB tab */}
        <TabsContent value="kb" className="space-y-4 mt-4">
          {!kbAgent ? null : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Sources */}
              <div className="lg:col-span-2 space-y-3">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Upload PDF / Text</h3>
                  <p className="text-xs text-muted-foreground mt-1">Upload course brochures, FAQ docs, university handbooks. Each file is split into chunks and embedded.</p>
                  <div className="mt-3">
                    <input id="kb-file" type="file" accept=".pdf,.txt,.md" className="hidden" onChange={handleFileUpload} />
                    <label htmlFor="kb-file">
                      <Button asChild disabled={uploading}>
                        <span className="cursor-pointer">
                          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Choose file
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold flex items-center gap-2"><Database className="w-4 h-4" /> Auto-ingest CRM data</h3>
                  <p className="text-xs text-muted-foreground mt-1">Embed every program (university, degree, intakes, tuition, description) so the agent can answer fee/intake questions.</p>
                  {(kbAgent.university_ids || []).length === 0 ? (
                    <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                      ⚠️ This agent has no university scope set. Re-ingest will pull <strong>all programs from all universities</strong> in your tenant. To fix: edit the agent and tick the universities this agent should cover.
                    </div>
                  ) : (
                    <div className="mt-2 p-2 rounded bg-green-50 border border-green-200 text-[11px] text-green-800">
                      Scoped to {(kbAgent.university_ids || []).length} university(ies). Only their programs will be ingested.
                    </div>
                  )}
                  <Button className="mt-3" onClick={ingestCrm} disabled={ingestingCrm}>
                    {ingestingCrm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Re-ingest from CRM
                  </Button>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold flex items-center gap-2"><Globe className="w-4 h-4" /> Crawl official website</h3>
                  <p className="text-xs text-muted-foreground mt-1">When the KB doesn't know an answer, the agent can fall back to your university's website. Paste one URL or domain per line — we'll follow internal links and the sitemap.</p>
                  <Textarea
                    rows={3}
                    placeholder={'https://www.sunderland.ac.uk\nhttps://example.ac.uk/international-students'}
                    value={crawlUrls}
                    onChange={(e) => setCrawlUrls(e.target.value)}
                    className="mt-2 font-mono text-xs"
                    data-testid="crawl-urls-input"
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <Label className="text-xs whitespace-nowrap">Max pages</Label>
                    <Input
                      type="number" min="5" max="200"
                      value={crawlMaxPages}
                      onChange={(e) => setCrawlMaxPages(e.target.value)}
                      className="h-8 w-24 text-sm"
                      data-testid="crawl-max-pages-input"
                    />
                    {kbAgent.last_crawl_status && (
                      <Badge
                        variant="outline"
                        className={
                          kbAgent.last_crawl_status === 'ok'
                            ? 'border-green-300 text-green-700'
                            : kbAgent.last_crawl_status === 'running'
                              ? 'border-blue-300 text-blue-700'
                              : 'border-amber-300 text-amber-700'
                        }
                      >
                        {kbAgent.last_crawl_status === 'ok'
                          ? `Indexed ${kbAgent.last_crawl_pages_count ?? 0} pages`
                          : kbAgent.last_crawl_status}
                      </Badge>
                    )}
                  </div>
                  <Button
                    className="mt-3"
                    onClick={crawlWebsite}
                    disabled={crawling || !crawlUrls.trim()}
                    data-testid="crawl-website-btn"
                  >
                    {crawling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                    Crawl &amp; index now
                  </Button>
                  {kbAgent.last_crawl_at && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Last crawl: {new Date(kbAgent.last_crawl_at).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Continuous learning</h3>
                  <p className="text-xs text-muted-foreground mt-1">Every time a counsellor approves an AI draft, edits one, or types a manual reply within an hour of an inbound message, that pair is auto-saved as a Q&amp;A. Once a week, GPT-4o also distils older transcripts to find reusable Q&amp;A pairs.</p>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
                    <div className="bg-slate-50 dark:bg-gray-800 rounded p-2">
                      <div className="text-muted-foreground">Last distillation</div>
                      <div className="font-medium">
                        {kbAgent.last_distill_at
                          ? new Date(kbAgent.last_distill_at).toLocaleString()
                          : 'Never'}
                      </div>
                    </div>
                    <div className="bg-slate-50 dark:bg-gray-800 rounded p-2">
                      <div className="text-muted-foreground">Last batch</div>
                      <div className="font-medium">+{kbAgent.last_distill_count ?? 0} Q&amp;A</div>
                    </div>
                  </div>
                  <Button
                    className="mt-3"
                    onClick={distillNow}
                    disabled={distilling}
                    data-testid="distill-now-btn"
                  >
                    {distilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                    Distill last 7 days now
                  </Button>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold flex items-center gap-2"><MessagesSquare className="w-4 h-4" /> Add Q&A pair</h3>
                  <p className="text-xs text-muted-foreground mt-1">Manually teach the agent: "When student asks X → reply Y".</p>
                  <div className="space-y-2 mt-3">
                    <Input placeholder="Question (e.g., What is the IELTS requirement?)" value={qaQ} onChange={(e) => setQaQ(e.target.value)} data-testid="qa-q-input" />
                    <Textarea rows={3} placeholder="Answer" value={qaA} onChange={(e) => setQaA(e.target.value)} data-testid="qa-a-input" />
                    <Button onClick={addQa} disabled={savingQa} data-testid="qa-add-btn">
                      {savingQa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Add to KB
                    </Button>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">Sources ({kbItems.length})</h3>
                  {loadingKb ? <Loader2 className="w-5 h-5 animate-spin" /> : kbItems.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No knowledge yet. Upload a file or add a Q&A pair above.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {kbItems.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-gray-800 rounded text-xs">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{s.source_name}</div>
                            <div className="text-muted-foreground">
                              {s.source_type} · {s.chunk_count} chunk{s.chunk_count > 1 ? 's' : ''} · {s.total_chars.toLocaleString()} chars
                            </div>
                          </div>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => deleteKb(s)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tester */}
              <div className="border rounded-lg p-4 bg-amber-50/30 dark:bg-slate-900/20 h-fit sticky top-4">
                <h3 className="font-semibold flex items-center gap-2"><Wand2 className="w-4 h-4" /> Try the agent</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-3">Type a sample WhatsApp message and see what the agent would reply.</p>
                <Textarea rows={3} placeholder='e.g., "What is the IELTS requirement for MBA?"' value={tester.message} onChange={(e) => setTester(t => ({ ...t, message: e.target.value }))} data-testid="tester-input" />
                <Button className="w-full mt-2" onClick={runTester} disabled={tester.loading || !tester.message.trim()} data-testid="tester-run-btn">
                  {tester.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Run
                </Button>
                {tester.result && (
                  <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded border text-xs space-y-2">
                    {tester.result.error ? (
                      <p className="text-red-600">{tester.result.error}</p>
                    ) : tester.result.escalate ? (
                      <div className="text-amber-700 dark:text-amber-300">
                        <div className="flex items-center gap-1 font-medium"><XCircle className="w-3.5 h-3.5" /> Escalation triggered</div>
                        <p className="mt-1">{tester.result.escalation_reason}</p>
                      </div>
                    ) : (
                      <>
                        <div className="text-green-700 dark:text-green-300 flex items-center gap-1 font-medium">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Confidence {(tester.result.confidence * 100).toFixed(0)}%
                        </div>
                        <p className="text-foreground whitespace-pre-wrap">{tester.result.reply}</p>
                        {tester.result.kb_refs?.length > 0 && (
                          <div className="text-muted-foreground border-t pt-1.5 mt-1.5">
                            <span className="font-medium">Sources:</span> {tester.result.kb_refs.map(r => r.source_name).join(', ')}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Settings tab */}
        <TabsContent value="settings" className="space-y-4 mt-4">
          <div className="border rounded-lg p-4 max-w-2xl">
            <h3 className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> OpenAI API Key</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Use your own OpenAI key for all agent calls. If not set, the platform's Emergent LLM key is used (with limits).
            </p>
            {settings?.openai_api_key_set ? (
              <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/20 border border-green-200 rounded p-3 text-sm">
                <div>
                  <div className="font-medium text-green-800 dark:text-green-300">Key saved · {settings.openai_api_key_masked}</div>
                  <div className="text-xs text-muted-foreground">Last updated {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : '—'}</div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={clearKey} data-testid="clear-key-btn">Remove</Button>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 mb-3">
                Currently using the platform's Emergent key. Add your own key for production use.
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Input type="password" placeholder="sk-proj-..." value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)} data-testid="openai-key-input" />
              <Button onClick={saveKey} disabled={savingKey || !keyDraft.trim()} data-testid="save-key-btn">
                {savingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verify & Save
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Stored encrypted at rest. Never shown back to the UI in plaintext.</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Agent editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
            <DialogDescription>Configure how this agent should behave on WhatsApp.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input value={editing.name || ''} onChange={(e) => setEditing(p => ({ ...p, name: e.target.value }))} data-testid="agent-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <select value={editing.status || 'active'} onChange={(e) => setEditing(p => ({ ...p, status: e.target.value }))} className="h-9 px-3 rounded-md border bg-background w-full text-sm">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Description</Label>
                <Input value={editing.description || ''} onChange={(e) => setEditing(p => ({ ...p, description: e.target.value }))} placeholder="Short description shown in the agent list" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tone</Label>
                  <select value={editing.tone || 'professional_warm'} onChange={(e) => setEditing(p => ({ ...p, tone: e.target.value }))} className="h-9 px-3 rounded-md border bg-background w-full text-sm">
                    {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Model</Label>
                  <select value={editing.model || 'gpt-4o-mini'} onChange={(e) => setEditing(p => ({ ...p, model: e.target.value }))} className="h-9 px-3 rounded-md border bg-background w-full text-sm">
                    {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Assigned counsellors (1 agent → many counsellors)</Label>
                <div className="border rounded-md max-h-40 overflow-y-auto p-2 mt-1 space-y-1">
                  {counsellors.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">No counsellors found.</p>
                  ) : counsellors.map((u) => {
                    const checked = (editing.counsellor_ids || []).includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-slate-50 dark:hover:bg-gray-800 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(editing.counsellor_ids || []);
                            if (e.target.checked) next.add(u.id); else next.delete(u.id);
                            setEditing(p => ({ ...p, counsellor_ids: Array.from(next) }));
                          }}
                          data-testid={`counsellor-cb-${u.id}`}
                        />
                        <span className="font-medium">{u.name || u.email}</span>
                        <span className="text-xs text-muted-foreground">{u.email}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{(editing.counsellor_ids || []).length} selected</p>
              </div>

              <div>
                <Label className="text-xs">Scope to universities (CRM auto-ingest will only pull these)</Label>
                <div className="border rounded-md max-h-40 overflow-y-auto p-2 mt-1 space-y-1">
                  {universities.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">No universities in master data.</p>
                  ) : universities.map((u) => {
                    const checked = (editing.university_ids || []).includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-slate-50 dark:hover:bg-gray-800 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(editing.university_ids || []);
                            if (e.target.checked) next.add(u.id); else next.delete(u.id);
                            setEditing(p => ({ ...p, university_ids: Array.from(next) }));
                          }}
                          data-testid={`uni-cb-${u.id}`}
                        />
                        <span>{u.name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {(editing.university_ids || []).length === 0
                    ? 'No scope set — agent will ingest ALL programs (not recommended for university-specific agents).'
                    : `${(editing.university_ids || []).length} selected — CRM ingest will only include programs from these universities.`}
                </p>
              </div>

              <div>
                <Label className="text-xs">System prompt (personality + role)</Label>
                <Textarea rows={4} value={editing.system_prompt || ''} onChange={(e) => setEditing(p => ({ ...p, system_prompt: e.target.value }))} placeholder="Leave blank for default professional assistant prompt" />
              </div>

              <div>
                <Label className="text-xs">Guardrails (hard rules)</Label>
                <Textarea rows={2} value={editing.guardrails || ''} onChange={(e) => setEditing(p => ({ ...p, guardrails: e.target.value }))} />
              </div>

              <div>
                <Label className="text-xs">Confidence threshold for escalation: {((editing.confidence_threshold || 0.35) * 100).toFixed(0)}%</Label>
                <input
                  type="range" min="0.25" max="0.9" step="0.05"
                  value={editing.confidence_threshold || 0.35}
                  onChange={(e) => setEditing(p => ({ ...p, confidence_threshold: parseFloat(e.target.value) }))}
                  className="w-full mt-1"
                />
                <p className="text-[10px] text-muted-foreground">Below this similarity, the agent escalates instead of replying.</p>
              </div>

              <div className="border-t pt-3 mt-1">
                <Label className="text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Official website domains (used for crawl + fallback)</Label>
                <Textarea
                  rows={2}
                  value={(editing.fallback_domains || []).join('\n')}
                  onChange={(e) => setEditing(p => ({
                    ...p,
                    fallback_domains: e.target.value.split(/\n|,/).map(s => s.trim()).filter(Boolean),
                  }))}
                  placeholder={'sunderland.ac.uk\nhttps://example.ac.uk/international'}
                  className="font-mono text-xs mt-1"
                  data-testid="fallback-domains-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">One domain or full URL per line. Crawl from the Knowledge Base tab.</p>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={editing.learn_from_manual_replies !== false}
                    onChange={(e) => setEditing(p => ({ ...p, learn_from_manual_replies: e.target.checked }))}
                    data-testid="learn-manual-cb"
                  />
                  <div>
                    <div className="text-xs font-medium">Learn from manual replies</div>
                    <div className="text-[10px] text-muted-foreground">Pair each typed reply with the prior inbound and save as a Q&amp;A.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={editing.distillation_enabled !== false}
                    onChange={(e) => setEditing(p => ({ ...p, distillation_enabled: e.target.checked }))}
                    data-testid="distill-enabled-cb"
                  />
                  <div>
                    <div className="text-xs font-medium">Weekly auto-distillation</div>
                    <div className="text-[10px] text-muted-foreground">GPT-4o reviews transcripts and extracts reusable Q&amp;A.</div>
                  </div>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveAgent} className="bg-amber-500 hover:bg-slate-800" data-testid="save-agent-btn">
              {editing?.id ? 'Save changes' : 'Create agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
